import { logger, maskUserId, maskToken, maskPermissions } from "@/utils/logger";
/**
 * 基础信息服务 - 用于在登录时获取并缓存系统基础信息
 */
import { httpApi } from "@/utils/http";

// 基础信息数据类型定义（根据后端API文档）
export interface TraderInfo {
  user_type: "admin" | "user"; // 用户类型：管理员或普通用户
  permission: string; // 权限配置字符串（分号分隔）
  permissions?: string[]; // 🆕 权限数组（新格式，可选，优先使用）
  operators: string[]; // 关联的操作员列表
  username: string; // 用户名（只读字段）
  futureetf_traders?: string[]; // 期货ETF交易员列表
  futureCaltraiders?: string[]; // 跨期策略交易员列表
  pcp_multi_traders?: string[]; // PCP多账户交易员列表
  future_spot_crypto_traders?: string[]; // W3期现套利交易员列表
  convert_bond_arbitrager?: string[]; // 转债套利交易员列表
  funds_to_monitor?: string[]; // 需要监控的基金列表
  [key: string]: any; // 允许其他动态字段
}

export interface AccountInfo {
  account_name: string; // 账户名称
  host: string; // 服务器地址
}

export interface FundInfo {
  fund_id: string; // 基金唯一标识
  fund_name: string; // 基金名称
  is_active: boolean; // 基金是否激活状态
  otc_account: AccountInfo[]; // 场外交易账户列表
  stock_account: AccountInfo[]; // 股票交易账户列表
  margin_account: AccountInfo[]; // 融资融券账户列表
  option_account: AccountInfo[]; // 期权交易账户列表
  future_account: AccountInfo[]; // 期货交易账户列表
}

export interface FutureETFTickerData {
  future_tickers: string[]; // 股指期货代码列表
  etf_tickers: string[]; // ETF代码列表
  future_alias: string[]; // 期货合约别名
  pcp_option_pair: string[]; // 期权PCP套利对
}

export interface BasicInfoData {
  traderInfo: TraderInfo | null;
  funds: FundInfo[];
  futureETFTickers: FutureETFTickerData | null;
  lastUpdated: string;
}

export interface BasicInfoProgressUpdate {
  completed: number;
  total: number;
  label: string;
}

class BasicInfoService {
  private cache: BasicInfoData = {
    traderInfo: null,
    funds: [],
    futureETFTickers: null,
    lastUpdated: "",
  };

  private readonly CACHE_KEY_PREFIX = "modern_frontend_basic_info";
  // 基础信息在登录后保持可用，直到登出或手动清理；不设失效时间
  private readonly CACHE_DURATION = Number.POSITIVE_INFINITY;

  /**
   * 获取交易员信息
   */
  async fetchTraderInfo(userId: string): Promise<TraderInfo> {
    try {
      const response = await httpApi.get<TraderInfo>(`/traderinfo/${userId}/`, undefined, {
        timeout: 20000,
      });

      if (response) {
        logger.log("获取交易员信息成功:", response);
        return response;
      }

      throw new Error("交易员信息返回为空");
    } catch (error) {
      logger.error("获取交易员信息失败:", error);
      throw new Error(`获取交易员信息失败: ${error instanceof Error ? error.message : "未知错误"}`);
    }
  }

  /**
   * 获取资金信息
   */
  async fetchFunds(): Promise<FundInfo[]> {
    try {
      const response = await httpApi.get<FundInfo[]>("/funds/", undefined, { timeout: 20000 });

      if (Array.isArray(response)) {
        logger.log("获取资金信息成功:", response.length, "个基金");
        return response;
      }

      throw new Error("资金信息格式错误");
    } catch (error) {
      logger.error("获取资金信息失败:", error);
      throw new Error(`获取资金信息失败: ${error instanceof Error ? error.message : "未知错误"}`);
    }
  }

  /**
   * 获取期货ETF标的信息
   */
  async fetchFutureETFTickers(): Promise<FutureETFTickerData> {
    try {
      const response = await httpApi.get<FutureETFTickerData>("/futureetftickers/", undefined, {
        timeout: 20000,
      });

      if (response && typeof response === "object") {
        logger.log("获取期货ETF标的信息成功:", {
          futureCount: response.future_tickers?.length || 0,
          etfCount: response.etf_tickers?.length || 0,
          pcpCount: response.pcp_option_pair?.length || 0,
        });
        return response;
      }

      throw new Error("期货ETF标的信息格式错误");
    } catch (error) {
      logger.error("获取期货ETF标的信息失败:", error);
      throw new Error(
        `获取期货ETF标的信息失败: ${error instanceof Error ? error.message : "未知错误"}`,
      );
    }
  }

  /**
   * 批量获取所有基础信息
   * ⚠️ 性能优化：移除了 FutureETFTickers 的登录时加载（仅1个测试页面使用）
   * 如需使用，可调用 fetchFutureETFTickersOnDemand() 按需加载
   */
  async fetchAllBasicInfo(
    userId: string,
    onProgress?: (update: BasicInfoProgressUpdate) => void,
  ): Promise<BasicInfoData> {
    const startTime = Date.now();
    logger.log("开始获取基础信息，用户ID:", userId);

    try {
      // 进度跟踪（优化后只有2个必需步骤）
      const total = 2;
      let completed = 0;
      const report = (label: string) => {
        completed += 1;
        onProgress?.({ completed, total, label });
      };

      // 并发获取必需信息，同时在每个请求完成时汇报进度
      const traderInfoPromise = this.fetchTraderInfo(userId)
        .then((res) => {
          report("交易员信息");
          return res;
        })
        .catch((err) => {
          report("交易员信息");
          throw err;
        });

      const fundsPromise = this.fetchFunds()
        .then((res) => {
          report("基金信息");
          return res;
        })
        .catch((err) => {
          report("基金信息");
          throw err;
        });

      // ❌ 移除：FutureETFTickers 登录时加载（极低频使用，改为按需加载）
      // 可通过 fetchFutureETFTickersOnDemand() 在需要时获取

      const [traderInfoResult, fundsResult] = await Promise.allSettled([
        traderInfoPromise,
        fundsPromise, // funds接口不需要用户ID
      ]);

      // 检查是否有任何请求失败
      const errors: string[] = [];

      if (traderInfoResult.status === "rejected") {
        errors.push(`交易员信息: ${traderInfoResult.reason?.message || "未知错误"}`);
      }

      if (fundsResult.status === "rejected") {
        errors.push(`资金信息: ${fundsResult.reason?.message || "未知错误"}`);
      }

      // 如果有任何请求失败,抛出错误
      if (errors.length > 0) {
        const errorMessage = `基础信息获取失败：\n${errors.join("\n")}`;
        logger.error(errorMessage);
        throw new Error(errorMessage);
      }

      const basicInfo: BasicInfoData = {
        traderInfo:
          traderInfoResult.status === "fulfilled" ? traderInfoResult.value : ({} as TraderInfo),
        funds: fundsResult.status === "fulfilled" ? fundsResult.value : [],
        futureETFTickers: null, // 不再登录时加载，需要时通过按需加载获取
        lastUpdated: new Date().toISOString(),
      };

      // 缓存到内存和本地存储
      this.cache = basicInfo;
      this.saveToLocalStorage(basicInfo, userId);

      const endTime = Date.now();
      logger.log(`基础信息获取完成，耗时: ${endTime - startTime}ms`);
      logger.log("基础信息摘要:", {
        hasTraderInfo: !!basicInfo.traderInfo,
        fundsCount: basicInfo.funds.length,
        futureETFTickersLoaded: "按需加载（未在登录时加载）",
      });

      return basicInfo;
    } catch (error) {
      logger.error("获取基础信息失败:", error);
      throw error;
    }
  }

  /**
   * 🆕 按需加载期货ETF标的信息（优化后新增）
   * 适用于极少使用该数据的场景
   */
  async fetchFutureETFTickersOnDemand(): Promise<FutureETFTickerData> {
    // 检查缓存
    if (this.cache.futureETFTickers) {
      logger.debug("使用缓存的期货ETF标的信息");
      return this.cache.futureETFTickers;
    }

    // 加载并缓存
    logger.debug("按需加载期货ETF标的信息");
    const data = await this.fetchFutureETFTickers();
    this.cache.futureETFTickers = data;
    return data;
  }

  /**
   * 获取缓存的基础信息（优先从内存，其次本地存储）
   */
  getCachedBasicInfo(userId?: string): BasicInfoData | null {
    // 检查内存缓存
    if (this.isCacheValid(this.cache)) {
      return this.cache;
    }

    // 检查本地存储缓存
    const stored = this.loadFromLocalStorage(userId);
    if (stored && this.isCacheValid(stored)) {
      this.cache = stored;
      return stored;
    }

    return null;
  }

  /**
   * 检查缓存是否有效
   */
  private isCacheValid(data: BasicInfoData): boolean {
    // 只要有有效内容即视为可用，直到用户登出清理
    if (!data) return false;
    const hasContent = !!(
      data.traderInfo ||
      (data.funds && data.funds.length > 0) ||
      data.futureETFTickers
    );
    return hasContent;
  }

  /**
   * 保存到本地存储
   */
  private saveToLocalStorage(data: BasicInfoData, userId: string): void {
    try {
      const cacheKey = `${this.CACHE_KEY_PREFIX}_${userId}`;
      localStorage.setItem(cacheKey, JSON.stringify(data));
    } catch (error) {
      logger.warn("保存基础信息到本地存储失败:", error);
    }
  }

  /**
   * 从本地存储加载
   */
  private loadFromLocalStorage(userId?: string): BasicInfoData | null {
    if (!userId) {
      return null;
    }

    try {
      const cacheKey = `${this.CACHE_KEY_PREFIX}_${userId}`;
      const stored = localStorage.getItem(cacheKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      logger.warn("从本地存储加载基础信息失败:", error);
    }
    return null;
  }

  /**
   * 清除缓存
   */
  clearCache(userId?: string): void {
    this.cache = {
      traderInfo: null,
      funds: [],
      futureETFTickers: null,
      lastUpdated: "",
    };

    try {
      if (userId) {
        // 清除特定用户的缓存
        const cacheKey = `${this.CACHE_KEY_PREFIX}_${userId}`;
        localStorage.removeItem(cacheKey);
      } else {
        // 清除所有基础信息缓存（通过前缀匹配）
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key?.startsWith(this.CACHE_KEY_PREFIX)) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
      }
    } catch (error) {
      logger.warn("清除本地存储缓存失败:", error);
    }
  }

  /**
   * 强制刷新基础信息
   */
  async refreshBasicInfo(userId: string): Promise<BasicInfoData> {
    this.clearCache();
    return await this.fetchAllBasicInfo(userId);
  }

  /**
   * 获取特定类型的信息
   */
  getTraderInfo(): TraderInfo | null {
    return this.cache.traderInfo;
  }

  getFunds(): FundInfo[] {
    return this.cache.funds || [];
  }

  getFutureETFTickers(): FutureETFTickerData | null {
    return this.cache.futureETFTickers;
  }

  /**
   * 检查基础信息是否已加载
   */
  isLoaded(): boolean {
    return !!(
      this.cache.traderInfo ||
      this.cache.funds.length > 0 ||
      this.cache.futureETFTickers
    );
  }
}

// 创建单例实例
export const basicInfoService = new BasicInfoService();

// 导出类型和服务实例
export default basicInfoService;
